import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { useOrganization } from "../OrganizationContext"
import OrganizationDialog from "../components/OrganizationDialog"
import ConfirmDialog from "../components/ConfirmDialog"
import OrganizationActionBar from "../components/OrganizationActionBar"
import OrganizationTable from "../components/OrganizationTable"
import { useUserRole } from "../UserRoleContext"
import { deleteOrganization } from "../api/organizations"

export default function ManageOrganizations() {
  const user = useUser()
  const role = useUserRole()
  const navigate = useNavigate()
  const { refreshOrganizations } = useOrganization()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState([])
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [createError, setCreateError] = useState("")
  const [leaveError, setLeaveError] = useState("")
  const [deleteError, setDeleteError] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const { data: organizations = [], isLoading: loading } = useQuery({
    queryKey: ['user-organizations', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_user_organizations')
      if (error) throw error
      return data || []
    },
    enabled: !!user,
  })

  useEffect(() => {
    if (!user) return
    supabase.rpc("whoami").then(({ data, error }) => {
      if (error) console.error("whoami error", error)
      else console.log("whoami", data)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    setSelected((prev) => {
      if (!prev.length) {
        return prev
      }

      const validIds = new Set(organizations.map((org) => org.id))
      const next = prev.filter((orgId) => validIds.has(orgId))

      return next.length === prev.length ? prev : next
    })
  }, [organizations])

  const selectedOrganizations = useMemo(
    () => organizations.filter((org) => selected.includes(org.id)),
    [organizations, selected]
  )
  const lastMemberOrganizations = useMemo(
    () => selectedOrganizations.filter((org) => org.member_count <= 1),
    [selectedOrganizations]
  )
  const selectedCount = selected.length
  const isAdmin = role === "admin"

  const handleToggleSelect = (orgId) => {
    setSelected((prev) =>
      prev.includes(orgId)
        ? prev.filter((id) => id !== orgId)
        : [...prev, orgId]
    )
  }

  const handleCreateClick = () => {
    setCreateError("")
    setIsCreateDialogOpen(true)
  }

  const handleLeaveClick = () => {
    if (!selectedCount) return
    setLeaveError("")
    setIsConfirmDialogOpen(true)
  }

  const handleDeleteClick = () => {
    if (!selectedCount) return
    setDeleteError("")
    setIsDeleteDialogOpen(true)
  }

  const handleCreateOrganization = async (sanitizedName, organizationType) => {
    if (isCreating) return
    setIsCreating(true)
    setCreateError("")

    const { error } = await supabase.rpc("create_org_with_admin", {
      org_name: sanitizedName,
      org_type: organizationType,
    })

    if (error) {
      console.error("Failed to create organization", error)
      setCreateError("Unable to create the organization. Please try again.")
      setIsCreating(false)
      return
    }

    await queryClient.invalidateQueries({ queryKey: ['user-organizations', user?.id] })
    refreshOrganizations()
    setIsCreating(false)
    setIsCreateDialogOpen(false)
  }

  const handleConfirmLeave = async () => {
    if (!selectedCount || isLeaving) {
      return
    }

    setIsLeaving(true)
    setLeaveError("")

    const organizationsToLeave = selectedOrganizations.filter(
      (org) => org.member_count > 1
    )
    const organizationsToDelete = lastMemberOrganizations

    try {
      if (organizationsToDelete.length) {
        for (const org of organizationsToDelete) {
          await deleteOrganization(org.id)
        }
      }

      if (organizationsToLeave.length) {
        const leaveResults = await Promise.all(
          organizationsToLeave.map((org) =>
            supabase.rpc("leave_org", { org_id: org.id })
          )
        )

        const failed = leaveResults.find((result) => result?.error)

        if (failed) {
          console.error("Failed to leave organizations", failed.error)
          setLeaveError(
            "Could not leave the selected organizations. Please try again."
          )
          return
        }
      }

      setSelected([])
      setIsConfirmDialogOpen(false)
      await queryClient.invalidateQueries({
        queryKey: ['user-organizations', user?.id],
      })
      refreshOrganizations()
    } catch (error) {
      console.error("Failed to leave organizations", error)
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Could not leave the selected organizations. Please try again."
      setLeaveError(message)
    } finally {
      setIsLeaving(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedCount || isDeleting) {
      return
    }

    setIsDeleting(true)
    setDeleteError("")

    try {
      for (const org of selectedOrganizations) {
        await deleteOrganization(org.id)
      }

      setSelected([])
      setIsDeleteDialogOpen(false)
      await queryClient.invalidateQueries({
        queryKey: ['user-organizations', user?.id],
      })
      refreshOrganizations()
    } catch (error) {
      console.error("Failed to delete organizations", error)
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Could not delete the selected organizations. Please try again."
      setDeleteError(message)
    } finally {
      setIsDeleting(false)
    }
  }

  const { leaveMessage, leaveConfirmLabel } = useMemo(() => {
    if (!selectedCount) {
      return {
        leaveMessage: "",
        leaveConfirmLabel: "Leave",
      }
    }

    if (!lastMemberOrganizations.length) {
      if (selectedCount === 1) {
        return {
          leaveMessage: "Are you sure you want to leave this organization?",
          leaveConfirmLabel: "Leave",
        }
      }
      return {
        leaveMessage: `Are you sure you want to leave these ${selectedCount} organizations?`,
        leaveConfirmLabel: "Leave",
      }
    }

    if (selectedCount === 1) {
      const orgName = lastMemberOrganizations[0]?.name || "this organization"
      return {
        leaveMessage: `You are the last member of ${orgName}. Leaving will delete this organization and all of its data. Are you sure you want to delete it?`,
        leaveConfirmLabel: "Leave and delete",
      }
    }

    if (lastMemberOrganizations.length === selectedCount) {
      const names = lastMemberOrganizations.map((org) => org.name).join(", ")
      return {
        leaveMessage: `You are the last member of the selected organizations (${names}). Leaving will delete these organizations and remove all associated data. Are you sure you want to continue?`,
        leaveConfirmLabel: "Leave and delete",
      }
    }

    const names = lastMemberOrganizations.map((org) => org.name).join(", ")
    return {
      leaveMessage: `You are the last member of ${lastMemberOrganizations.length} of the selected organizations (${names}). Leaving will delete those organizations and all associated data. The remaining organizations will simply remove you as a member. Are you sure you want to continue?`,
      leaveConfirmLabel: "Leave and delete",
    }
  }, [lastMemberOrganizations, selectedCount])

  const deleteMessage = useMemo(() => {
    if (!selectedCount) return ""
    if (selectedCount === 1) {
      const orgName = selectedOrganizations[0]?.name || "this organization"
      return `Deleting ${orgName} will cancel any active subscriptions and permanently remove all associated data. This action cannot be undone. Do you want to continue?`
    }
    return `Deleting these ${selectedCount} organizations will cancel any active subscriptions and permanently remove all associated data. This action cannot be undone. Do you want to continue?`
  }, [selectedCount, selectedOrganizations])

  return (
    <div className="page-container organization-page">
      <header className="organization-page__header">
        <div>
          <h2 className="organization-page__title">Your organizations</h2>
          <p className="organization-page__subtitle">
            Manage the organizations you belong to.
          </p>
        </div>
      </header>
      <OrganizationActionBar
        onCreateClick={handleCreateClick}
        onLeaveClick={handleLeaveClick}
        onDeleteClick={isAdmin ? handleDeleteClick : undefined}
        selectedCount={selectedCount}
      />
      {loading ? (
        <p className="organization-page__status">Loading organizations…</p>
      ) : organizations.length === 0 ? (
        <p className="organization-page__status">
          You’re not a member of any organizations yet.
        </p>
      ) : (
        <OrganizationTable
          organizations={organizations}
          selectedIds={selected}
          onToggleSelect={handleToggleSelect}
          onManage={(orgId) => navigate(`/organizations/${orgId}`)}
        />
      )}
      <OrganizationDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateOrganization}
        isSubmitting={isCreating}
        errorMessage={createError}
      />
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        title="Leave organizations"
        message={leaveMessage}
        confirmLabel={leaveConfirmLabel}
        cancelLabel="Stay"
        onConfirm={handleConfirmLeave}
        onCancel={() => setIsConfirmDialogOpen(false)}
        isProcessing={isLeaving}
        errorMessage={leaveError}
      />
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Delete organizations"
        message={deleteMessage}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteDialogOpen(false)}
        isProcessing={isDeleting}
        errorMessage={deleteError}
      />
    </div>
  )
}
